import { FC, MouseEvent, useRef, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { splitMatchedText } from '@/utils/helpers';

import { FormDataT } from './AddInvoiceForm';

const Dropdown = styled.div`
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Relative = styled.div`
  position: relative;
`;

const DropdownBox = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 348px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const DropdownButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const InputHolder = styled.div<{ $dropdownOpened: boolean }>`
  position: relative;
  svg {
    position: absolute;
    top: calc(50% - 10px);
    right: 8px;
    transition: transform ease-out 0.2s;
  }
  ${({ $dropdownOpened }) =>
    $dropdownOpened &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const NoOptionsText = styled(Text)`
  text-align: center;
`;

interface Props {
  name: 'department_id' | 'company_id' | 'finance_account_number_id';
  options: {
    value: number;
    label: string;
  }[];
  setValue: UseFormSetValue<FormDataT>;
  control: Control<FormDataT, any>;
}

const AddInvoiceDropdown: FC<Props> = ({
  options,
  setValue,
  name,
  control,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const value = useWatch({ control, name });

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 150);

  const filteredOptions = options.filter((c) =>
    c.label.toLowerCase().includes(debouncedSearch.toLowerCase()),
  );

  const ref = useRef<HTMLInputElement>(null);
  useOnClickOutside(ref, () => setShowDropdown(false));

  return (
    <Dropdown>
      <Relative>
        <InputHolder key={value} $dropdownOpened={showDropdown}>
          <Input
            ref={ref}
            placeholder="Select"
            type="text"
            onChange={(e) => setSearch(e.target.value)}
            onClick={() => setShowDropdown(true)}
            defaultValue={
              options.find((option) => value === option.value)?.label
            }
          />
          <svg height="20" width="20" viewBox="0 0 20 20" focusable="false">
            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
          </svg>
        </InputHolder>
        {showDropdown && (
          <DropdownBox>
            {!!filteredOptions.length ? (
              filteredOptions.map((option, index) => (
                <DropdownButton
                  key={index}
                  type="button"
                  as="button"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setValue(name, option.value);
                    setShowDropdown(false);
                  }}
                >
                  <Text
                    dangerouslySetInnerHTML={{
                      __html: splitMatchedText(option.label, debouncedSearch),
                    }}
                    $color="gray"
                    $type="captionSemibold"
                  ></Text>
                </DropdownButton>
              ))
            ) : (
              <DropdownButton disabled>
                <NoOptionsText $color="gray" $type="captionSemibold">
                  No options
                </NoOptionsText>
              </DropdownButton>
            )}
          </DropdownBox>
        )}
      </Relative>
    </Dropdown>
  );
};

export default AddInvoiceDropdown;
